import { FullProject, Task } from '../types';
import { areProjectIdsMatching, isCommProject, isInboxProject } from './idNormalizer';

/**
 * Checks whether a user has write/edit permissions on a given project.
 * - System projects (inbox_inforum, comm_internal) are always editable by the signed-in user.
 * - Project owners (project.userId === currentUserId) always have write permissions.
 * - Local unshared projects without an explicit foreign owner or sharing restrictions are editable.
 * - When sharedEditors is present, only users in sharedEditors have write permissions.
 * - When sharedEditors is not specified but user is in sharedWith, user has write permissions (legacy full-share).
 * - Otherwise (e.g. user in sharedWith but NOT in sharedEditors), access is read-only.
 */
export function isProjectEditableByUser(
  project?: FullProject | null,
  currentUserId?: string | null,
  userEmail?: string | null
): boolean {
  if (!project) return true;
  if (!currentUserId && !userEmail) return true;
  if (isInboxProject(project.id) || isCommProject(project.id)) return true;
  if (project.userId && currentUserId && project.userId === currentUserId) return true;
  if (
    (!project.userId || project.userId === 'unknown') &&
    !project.sharedWith &&
    !project.sharedEditors
  ) {
    return true;
  }

  const normalizedEmail = userEmail?.toLowerCase().trim();
  if (!normalizedEmail) return false;

  if (!project.sharedEditors) {
    return !!project.sharedWith?.some(e => e.toLowerCase().trim() === normalizedEmail);
  }

  return project.sharedEditors.some(e => e.toLowerCase().trim() === normalizedEmail);
}

/**
 * Resolves the authoritative owning project for a task from `allProjects`.
 */
export function resolveTaskOwningProject(
  task?: Task | null,
  allProjects?: FullProject[] | null
): FullProject | undefined {
  if (!task || !allProjects || allProjects.length === 0) return undefined;
  const candidateProjectId =
    (task as Task & { storageProjectId?: string }).storageProjectId || task.projectId;
  if (candidateProjectId) {
    const matched = allProjects.find(p => areProjectIdsMatching(p.id, candidateProjectId));
    if (matched) return matched;
  }
  return allProjects.find(p => (p.tasks || []).some(t => String(t.id) === String(task.id)));
}

/**
 * Checks whether a user has write/edit permissions for a specific task,
 * taking into account its owning project's sharing permissions (H-1).
 */
export function canUserEditTask(
  task?: Task | null,
  allProjects?: FullProject[] | null,
  currentUserId?: string | null,
  userEmail?: string | null
): boolean {
  if (!task) return true;
  if ((task as Task & { isReadOnly?: boolean }).isReadOnly === true) return false;
  if (!allProjects || allProjects.length === 0) return true;

  const owningProject = resolveTaskOwningProject(task, allProjects);
  if (!owningProject) return true;

  return isProjectEditableByUser(owningProject, currentUserId, userEmail);
}

/**
 * Checks whether a user is allowed to move/assign a task from its owning project
 * into `targetProjectId` (requires write permissions on BOTH source and target project).
 */
export function canUserMoveTaskBetweenProjects(
  task: Task | null | undefined,
  targetProjectId: string,
  allProjects?: FullProject[] | null,
  currentUserId?: string | null,
  userEmail?: string | null
): boolean {
  if (!canUserEditTask(task, allProjects, currentUserId, userEmail)) {
    return false;
  }
  if (!allProjects || allProjects.length === 0 || !targetProjectId) return true;
  if (isInboxProject(targetProjectId) || isCommProject(targetProjectId)) return true;

  const targetProject = allProjects.find(p => areProjectIdsMatching(p.id, targetProjectId));
  if (!targetProject) return true;

  return isProjectEditableByUser(targetProject, currentUserId, userEmail);
}
